import { Accordion, Button, Grid, Group, Text, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { useEffect, useState } from 'react';
import { Minus, Plus } from 'tabler-icons-react';
import { BusinessTypes } from '../../enums/business-type';
import { UpdateMerchantOnboardingFormValues } from '../../type/update-merchant-onboarding-form-values.types';
import UpdateIncorporator from './components/update-incorporators-signatories-components/update-incorporator.component';
import UpdateSignatory from './components/update-incorporators-signatories-components/update-signatory.component';

interface UpdateIncorporatorsSignatoriesProps {
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>;
}

function UpdateIncorporatorsSignatories(
  props: UpdateIncorporatorsSignatoriesProps
) {
  const { form } = props;

  const type = form.values.businessTypeId;
  const MAX_INCORPORATORS =
    type === BusinessTypes.SOLE_PROPRIETOR
      ? 1
      : type === BusinessTypes.PARTNERSHIP
      ? 2
      : 5;
  const MIN_INCORPORATORS = 0;
  const MIN_SIGNATORIES = 0;
  const MAX_SIGNATORIES = 5;

  const [numSignatories, setNumSignatories] = useState(
    form.values.signatories.length
  );
  const [numIncorporators, setNumIncorporators] = useState(
    form.values.incorporators!.length || MIN_INCORPORATORS
  );
  const [onInitialLoad, setOnInitialLoad] = useState(true);

  useEffect(() => {
    if (
      form.values.businessTypeId !== BusinessTypes.SOLE_PROPRIETOR ||
      onInitialLoad
    ) {
      setOnInitialLoad(false);
      return;
    }
    if (!form.values.copyProToSignatory) {
      form.setListItem('signatories', 0, {
        firstName: '',
        middleName: '',
        hasMiddleName: true,
        lastName: '',
        position: form.values.signatories[0].position,
      });
    } else if (
      form.values.incorporators &&
      form.values.incorporators.length > 0
    ) {
      form.setListItem('signatories', 0, {
        firstName: form.values.incorporators[0].firstName,
        middleName: form.values.incorporators[0].middleName,
        hasMiddleName: form.values.incorporators[0].hasMiddleName,
        lastName: form.values.incorporators[0].lastName,
        position: form.values.signatories[0].position,
      });
    }
  }, [form.values.copyProToSignatory]);

  const signatories = form.values.signatories.map((item, index) => {
    return (
      <Accordion.Item
        key={index}
        label={'Signatory ' + (index + 1)}
        data-cy={'signatory' + index}
        opened
      >
        <UpdateSignatory form={form} index={index} />
      </Accordion.Item>
    );
  });
  const incorporators = form.values.incorporators?.map((item, index) => {
    return (
      <Accordion.Item
        key={index}
        data-cy={'incorporator' + index}
        label={
          type === BusinessTypes.SOLE_PROPRIETOR
            ? 'Proprietor '
            : 'Incorporator ' + (index + 1)
        }
        opened
      >
        <UpdateIncorporator form={form} index={index} />
      </Accordion.Item>
    );
  });
  return (
    <>
      <Grid>
        <Grid.Col>
          <Text size='xl' weight={700} data-cy='incorporatorSignatoriesHeader'>
            {type === BusinessTypes.STARTER
              ? 'Signatories'
              : type === BusinessTypes.SOLE_PROPRIETOR
              ? 'Proprietor & Signatories'
              : 'Incorporators & Signatories'}
          </Text>
        </Grid.Col>
        {form.values.businessTypeId !== BusinessTypes.STARTER && (
          <>
            <Grid.Col>
              <Title order={4} data-cy='incorporatorsTitle'>
                {type === BusinessTypes.SOLE_PROPRIETOR
                  ? 'Proprietor'
                  : 'Incorporator'}
              </Title>
            </Grid.Col>
            <Grid.Col>
              <Accordion
                multiple
                initialItem={0}
                data-cy='incorporatorsAccordion'
              >
                {incorporators}
              </Accordion>
            </Grid.Col>
            {form.errors && (
              <Grid.Col>
                {incorporators?.map((value, index) => {
                  return (
                    <div key={index}>
                      {JSON.stringify(form.errors).includes(
                        'incorporators.' + index.toString()
                      ) && (
                        <Text color='red'>
                          Invalid field/s in incorporators{' '}
                          {type === BusinessTypes.SOLE_PROPRIETOR
                            ? 'Proprietor'
                            : 'Incorporator'}{' '}
                          {index + 1}
                        </Text>
                      )}
                    </div>
                  );
                })}
              </Grid.Col>
            )}
            <Grid.Col xs={12} md={6}>
              <Button
                data-cy='addIncorporatorButton'
                fullWidth
                onClick={() => {
                  form.addListItem('incorporators', {
                    firstName: '',
                    middleName: '',
                    hasMiddleName: true,
                    lastName: '',
                    bldgNameNo: '',
                    emailAddress: '',
                    contactNumber: '',
                    birthDate: null,
                    residentialAddress: '',
                    barangay: '',
                    city: '',
                    province: '',
                    postalCode: '',
                  });
                  setNumIncorporators((prev) => {
                    return prev + 1;
                  });
                }}
                variant='outline'
                color='dark'
                disabled={numIncorporators >= MAX_INCORPORATORS}
              >
                <Group noWrap>
                  <Plus
                    size={15}
                    strokeWidth={2}
                    color={
                      numIncorporators >= MAX_INCORPORATORS
                        ? '#adb5bd'
                        : 'black'
                    }
                  />
                  ADD{' '}
                  {type === BusinessTypes.SOLE_PROPRIETOR
                    ? 'PROPRIETOR'
                    : 'INCORPORATOR'}
                </Group>
              </Button>
            </Grid.Col>
            <Grid.Col xs={12} md={6}>
              <Button
                data-cy='removeIncorporatorButton'
                fullWidth
                onClick={() => {
                  form.removeListItem(
                    'incorporators',
                    form.values.incorporators!.length - 1
                  );
                  setNumIncorporators((prev) => {
                    return prev - 1;
                  });
                }}
                disabled={numIncorporators <= MIN_INCORPORATORS}
                variant='outline'
                color='dark'
              >
                <Group noWrap>
                  <Minus
                    size={15}
                    strokeWidth={2}
                    color={
                      numIncorporators <= MIN_INCORPORATORS
                        ? '#adb5bd'
                        : 'black'
                    }
                  />
                  REMOVE{' '}
                  {type === BusinessTypes.SOLE_PROPRIETOR
                    ? 'PROPRIETOR'
                    : 'INCORPORATOR'}
                </Group>
              </Button>
            </Grid.Col>
          </>
        )}
        <Grid.Col>
          <Title order={4}>Signatory</Title>
        </Grid.Col>
        <Grid.Col>
          <Accordion multiple initialItem={0} data-cy='signatoriesAccordion'>
            {signatories}
          </Accordion>
        </Grid.Col>
        {form.errors && (
          <Grid.Col>
            {signatories.map((value, index) => {
              return (
                <div key={index}>
                  {JSON.stringify(form.errors).includes(
                    'signatories.' + index.toString()
                  ) && (
                    <Text color='red'>
                      Invalid field/s in Signatory {index + 1}
                    </Text>
                  )}
                </div>
              );
            })}
          </Grid.Col>
        )}
        <Grid.Col xs={12} md={6}>
          <Button
            data-cy='addSignatoryButton'
            fullWidth
            onClick={() => {
              form.addListItem('signatories', {
                firstName: '',
                middleName: '',
                hasMiddleName: true,
                lastName: '',
                position: '',
              });
              setNumSignatories((prev) => {
                return prev + 1;
              });
            }}
            variant='outline'
            color='dark'
            disabled={numSignatories >= MAX_SIGNATORIES}
          >
            <Group noWrap>
              <Plus
                size={15}
                strokeWidth={2}
                color={numSignatories >= MAX_SIGNATORIES ? '#adb5bd' : 'black'}
              />
              ADD SIGNATORY
            </Group>
          </Button>
        </Grid.Col>
        <Grid.Col xs={12} md={6}>
          <Button
            data-cy='removeSignatoryButton'
            fullWidth
            onClick={() => {
              form.removeListItem(
                'signatories',
                form.values.signatories.length - 1
              );
              setNumSignatories((prev) => {
                return prev - 1;
              });
            }}
            disabled={numSignatories <= MIN_SIGNATORIES}
            variant='outline'
            color='dark'
          >
            <Group noWrap>
              <Minus
                size={15}
                strokeWidth={2}
                color={numSignatories <= MIN_SIGNATORIES ? '#adb5bd' : 'black'}
              />
              REMOVE SIGNATORY
            </Group>
          </Button>
        </Grid.Col>
      </Grid>
    </>
  );
}

export default UpdateIncorporatorsSignatories;
